import type { PostStatus } from '../../types/publishing.js';

// Publishing — the post DELETE decision (2026-07-28). Pure + shared so the server
// orchestrator, the client button visibility, and the tests all read ONE rule.
//
// The load-bearing distinction: a SCHEDULED post is live in Zernio's queue, so
// deleting it must CANCEL the Zernio schedule first or it will still fire. A pure
// DRAFT was never dispatched (no zernioPostId), so it just soft-deletes locally.
// A PUBLISHED post can't be deleted at all — Zernio can't unpublish a live post
// (the "duplicate to repost" story stands), and a post mid-flight ('publishing')
// has no safe cancellation point.

// Published / partial / publishing — non-deletable. The composer + row UIs hide
// the Delete button for these; the service rejects as a backstop.
const NON_DELETABLE: PostStatus[] = ['published', 'partial', 'publishing'];

export type PostDeleteAction =
  // Refuse — a published/in-flight post can't be deleted.
  | { action: 'reject'; reason: string }
  // Cancel the Zernio schedule, THEN soft-delete our mirror.
  | { action: 'cancel-then-delete' }
  // Soft-delete the local doc only — never dispatched, nothing to cancel.
  | { action: 'delete-only' };

/**
 * Decide how to delete a post from its status + whether it reached Zernio.
 *
 * - published / partial / publishing → reject
 * - anything with a zernioPostId (scheduled, or a draft that kept a prior
 *   schedule id after a failed edit) → cancel-then-delete (it's live in the queue)
 * - a pure draft (no zernioPostId) → delete-only
 */
export function decidePostDelete(
  status: PostStatus,
  zernioPostId: string | null,
): PostDeleteAction {
  if (NON_DELETABLE.includes(status)) {
    return {
      action: 'reject',
      reason: 'This post is published and can’t be deleted — duplicate it to repost instead.',
    };
  }
  if (zernioPostId) return { action: 'cancel-then-delete' };
  return { action: 'delete-only' };
}

/** Can this post be deleted at all? Drives the Delete button's visibility. */
export function isPostDeletable(status: PostStatus): boolean {
  return !NON_DELETABLE.includes(status);
}
